import { useEffect, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Grid, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchClientPrivacySettings,
  fetchSelectedClientRequest,
} from '../../redux/client/clientSlice';
import NotesCard from '../NotesCard/NotesCard';
import ActivitiesContainer from '../ClientActivitiesContainer/ClientActivitiesContainer';
import ClientDetail from '../ClientDetail/ClientDetail';
import Progress from '../ClientProgress/Progress';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import ChatBox, { IChatBoxProps } from '../Chat/Messenger/ChatBox';
import {
  ICoachMessagesPyalod,
  SenderType,
  setChatNotificationStatus,
  updateChat,
} from '../../redux/chat/chatSlice';
import { SUBSCRIBE_TO_COACH_MESSAGES } from '../../service/graphql/chatQuery';
import { ClientType } from '../../types/client';
import { hideScrollbarStyles } from '../../utils/styles';
import ClientGroup from '../ClientGroup/ClientGroup';

export function ChatBoxWrapper({ client, data }: IChatBoxProps) {
  if (!client) return null;
  return (
    <Grid
      mt={2}
      border="0.5px solid #7CAEBE"
      borderRadius="5px"
      bgcolor="#fff"
      height="max-content"
    >
      <ChatBox client={client} data={data} isClientTab />
    </Grid>
  );
}

const tabsProperties = (selectedClient, data) => [
  {
    label: 'clientPage.tabs.notes',
    tab: 'notes',
    icon: '/assets/icons/notes.svg',
    boldIcon: '/assets/icons/notes-bold.svg',
    component: <NotesCard />,
    permissions: [UserPermissionType.NOTE_MODULE_VIEW_NOTES],
    clientType: [ClientType.INDIVIDUAL, ClientType.GROUP],
  },
  {
    label: 'clientPage.tabs.chat',
    tab: 'chat',
    icon: '/assets/icons/chat-round-not-bold.svg',
    boldIcon: '/assets/icons/chat-round.svg',
    component: <ChatBoxWrapper client={selectedClient} data={data} />,
    permissions: [UserPermissionType.CHAT_MODULE_VIEW_CHAT],
    clientType: [ClientType.INDIVIDUAL],
  },
  {
    label: 'clientPage.tabs.progress',
    tab: 'progress',
    icon: '/assets/icons/chart-square.svg',
    boldIcon: '/assets/icons/chart-square-bold.svg',
    component: <Progress />,
    permissions: [UserPermissionType.PROGRESS_MODULE_GET_PROGRESS_SUMMARY],
    clientType: [ClientType.INDIVIDUAL, ClientType.GROUP],
  },
  {
    label: 'clientPage.tabs.activities',
    tab: 'activities',
    icon: '/assets/icons/sticker-square.svg',
    boldIcon: '/assets/icons/sticker-square-bold.svg',
    component: <ActivitiesContainer />,
    permissions: [UserPermissionType.ACTIVITY_MODULE_VIEW_ACTIVITIES],
    clientType: [ClientType.INDIVIDUAL, ClientType.GROUP],
  },
  {
    label: 'clientPage.tabs.members',
    tab: 'members',
    icon: '/assets/icons/clients.svg',
    boldIcon: '/assets/icons/users-group-bold.svg',
    component: <ClientGroup />,
    permissions: [
      UserPermissionType.CORE_MODULE_GET_CLIENTS,
      UserPermissionType.CLIENT_GROUP_MODULE_VIEW_CLIENT_GROUP,
    ],
    clientType: [ClientType.GROUP],
  },
  {
    label: 'clientPage.tabs.groupDetails',
    tab: 'group-details',
    icon: '/assets/icons/file-text-icon.svg',
    boldIcon: '/assets/icons/file-text-bold-icon.svg',
    component: <ClientDetail />,
    permissions: [UserPermissionType.CLIENT_GROUP_MODULE_VIEW_CLIENT_GROUP],
    clientType: [ClientType.GROUP],
  },
  {
    label: 'clientPage.tabs.clientDetails',
    tab: 'client-details',
    icon: '/assets/icons/user.svg',
    boldIcon: '/assets/icons/user-bold.svg',
    component: <ClientDetail />,
    permissions: [
      UserPermissionType.CORE_MODULE_VIEW_SINGLE_CLIENT,
      UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT,
    ],
    clientType: [ClientType.INDIVIDUAL],
  },
];

interface ITabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

export interface IStyledProps {
  theme?: Theme;
  isrtl?: boolean;
}

interface ITabProperty {
  label: string;
  tab: string;
  icon: string;
  boldIcon: string;
  component: React.ReactNode;
  permissions?: string[];
}

const IconWrapper = styled(Box)`
  width: 16px;
  height: 16px;
  display: flex;
  margin-top: 2px;
  justify-content: center;
  align-items: center;
`;

const SingleTab = styled(Tab)<IStyledProps>(({ theme, isrtl }) => ({
  height: '52px',
  minHeight: '52px',
  display: 'flex',
  gap: '12px',
  justifyContent: 'center',
  flexDirection: 'row',
  alignItems: 'center',
  fontFamily: 'Satoshi',
  fontSize: '17px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '22px',
  color: theme.palette.primary.main,
  '&.Mui-selected': {
    fontWeight: 700,
    transition: 'font-weight 0.2s ease-in-out',
    background:
      'linear-gradient(138deg, rgba(101, 153, 165, 0.15) 24.11%, rgba(59, 102, 126, 0.15) 83.2%), #FFF',
    '& span': {
      background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  },
  '&:not(:first-of-type)': {
    borderLeft: isrtl ? 'none' : '0.5px solid #7CAEBE',
    borderRight: isrtl ? '0.5px solid #7CAEBE' : 'none',
  },
  '.Mui-selected & svg': {
    stroke: 'black',
    strokeWidth: '4.5',
    fill: 'currentColor',
  },
  '@media (max-width: 900px)': {
    justifyContent: 'flex-start',
  },
}));

const ChatCountBadge = styled(Box)`
  width: auto;
  min-width: 38px;
  height: 20px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background: linear-gradient(175.46deg, #ff9a9a -9.81%, #ff5858 96.32%);
  opacity: 1;
  font-size: 12px;
  color: #fff;
  font-weight: bold;
  margin-left: 8px;
`;

export const CustomTabPanel = ({ children, value, index, ...other }: ITabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
    style={{
      height: '100%',
      width: '100%',
    }}
  >
    {value === index && <Box height="100%">{children}</Box>}
  </div>
);

export function tabPanelProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function SingleClientTopbar() {
  const [value, setValue] = useState(-2);
  const { id, groupid, groupname } = useParams();
  const tab = useURLQuery().get('tab');
  const navigate = useNavigate();
  const { selectedClient } = useAppSelector((state) => state.client);
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const dispatch = useAppDispatch();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [filteredTabProperties, setFilteredTabProperties] = useState<ITabProperty[]>([]);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const [validTabs, setValidTabs] = useState<string[]>([]);
  const [badgeText, setBadgeText] = useState(0);

  const { data } = useSubscription<ICoachMessagesPyalod>(SUBSCRIBE_TO_COACH_MESSAGES, {
    variables: { coachId },
    skip: !coachId,
  });

  useEffect(() => {
    if (filteredTabProperties[value]?.tab === 'chat') {
      dispatch(setChatNotificationStatus(false));
    }
  }, [value, dispatch, data]);

  useEffect(() => {
    if (!data || selectedClient?.id !== id) return;
    if (selectedClient?.id === data.coachMessages.clientId) return;
    dispatch(updateChat(data.coachMessages));
    if (
      selectedClient?.id === data.coachMessages.clientId &&
      data.coachMessages.chatMessages[0].senderType === SenderType.CLIENT
    )
      setBadgeText(badgeText > 10 ? badgeText : badgeText + 1);
  }, [data]);

  useEffect(() => {
    const filteredTabs = tabsProperties(selectedClient, data).filter(
      (tabProperty) =>
        checkPermissions(tabProperty.permissions, false) &&
        selectedClient?.clientType &&
        tabProperty.clientType.includes(selectedClient.clientType)
    );
    setFilteredTabProperties(filteredTabs);
    setValidTabs(filteredTabs.map((singleTab) => singleTab.tab));
  }, [selectedClient?.id, data]);

  useEffect(() => {
    if (!id) return;
    if (!selectedClient || selectedClient?.id !== id) {
      if (
        checkPermissions(
          [
            UserPermissionType.CORE_MODULE_VIEW_SINGLE_CLIENT,
            UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT,
          ],
          false
        )
      ) {
        dispatch(fetchSelectedClientRequest({ selectedClientId: id }));
      }
    }
  }, [id]);

  useEffect(() => {
    if (
      selectedClient &&
      selectedClient.id &&
      checkPermissions(UserPermissionType.CORE_MODULE_VIEW_CLIENT_CONCENT)
    ) {
      dispatch(fetchClientPrivacySettings({ coachId, clientId: selectedClient.id }));
      setBadgeText(selectedClient.totalUnreadMessages ?? 0);
    }
  }, [selectedClient?.id, coachId]);

  useEffect(() => {
    if (validTabs.length > 0) {
      const newValue = validTabs.indexOf(tab || validTabs[0]);
      setValue(newValue);
    }
  }, [value, id, navigate, validTabs]);

  if (value === -2 || validTabs.length === 0 || value !== validTabs.indexOf(tab || validTabs[0])) {
    return <div />; // TODO: add loading spinner
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const tabName = validTabs[newValue];
    if (groupid) navigate(`/clients/${groupid}/${groupname}/${id}?tab=${tabName}`);
    else navigate(`/clients/${id}?tab=${tabName}`);
  };

  return (
    <Box width="100%" height="100%">
      <Box
        bgcolor="#fff !important"
        borderRadius="10px"
        border="0.5px solid #7CAEBE"
        justifyContent="center"
        boxShadow="0px 4px 40px 0px rgba(255, 221, 200, 0.25)"
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          centered
          variant="fullWidth"
          sx={{
            width: '100%',
            height: '52px',
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {filteredTabProperties.map((singleTab: ITabProperty, index) => (
            <SingleTab
              key={singleTab.label}
              label={
                <Box display="flex" alignItems="center">
                  <span>{t(singleTab.label)}</span>
                  {singleTab.tab === 'chat' && badgeText > 0 && selectedClient?.id === id && (
                    <ChatCountBadge>{badgeText > 10 ? '10+' : badgeText}</ChatCountBadge>
                  )}
                </Box>
              }
              isrtl={language.split('-')[0] === 'ar'}
              icon={
                <IconWrapper>
                  <img
                    src={value === index ? singleTab.boldIcon : singleTab.icon}
                    alt={`${singleTab.label}-icon`}
                  />
                </IconWrapper>
              }
              sx={{
                borderRadius: () => {
                  if (index === 0) {
                    return language.split('-')[0] === 'ar' ? '0 10px 10px 0' : '10px 0 0 10px';
                  }
                  if (index === filteredTabProperties.length - 1) {
                    return language.split('-')[0] === 'ar' ? '10px 0 0 10px' : '0 10px 10px 0';
                  }
                  return '0';
                },
              }}
              {...tabPanelProps(index)}
            />
          ))}
        </Tabs>
      </Box>
      <Box
        height={{
          sm: '95%',
          lg: '92%',
        }}
        overflow="auto"
        sx={{ ...hideScrollbarStyles }}
      >
        {filteredTabProperties.map((tabName, index) => (
          <CustomTabPanel key={tabName.label} value={value} index={index}>
            {tabName.component}
          </CustomTabPanel>
        ))}
      </Box>
    </Box>
  );
}
